import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  Upload,
  UtensilsCrossed,
  X,
} from "lucide-react";
import { api, apiUpload, ApiRequestError } from "../lib/api";
import type { OwnerMenuResponse, OwnerOutlet } from "../lib/types";
import { PageHeader } from "../components/PageHeader";
import { formatRupiah } from "../lib/format";
import { menuImageFor } from "../lib/menu-images";

type ItemDraft = {
  id?: string;
  categoryId: number;
  name: string;
  description: string;
  imageUrl: string;
  basePrice: string;
  price: string;
  station: "kitchen" | "bar" | "both";
  available: boolean;
};

type CategoryDraft = {
  id?: number;
  name: string;
  sortOrder: string;
};

const emptyItem: ItemDraft = {
  categoryId: 0,
  name: "",
  description: "",
  imageUrl: "",
  basePrice: "",
  price: "",
  station: "kitchen",
  available: true,
};

export function MenuPage() {
  const [data, setData] = useState<OwnerMenuResponse | null>(null);
  const [outlets, setOutlets] = useState<OwnerOutlet[]>([]);
  const [outletId, setOutletId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [itemDraft, setItemDraft] = useState<ItemDraft | null>(null);
  const [categoryDraft, setCategoryDraft] = useState<CategoryDraft | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setUploadError(null);
  }, [itemDraft?.id]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [menu, outletResponse] = await Promise.all([
        api<OwnerMenuResponse>(
          `/api/owner/menu${outletId ? `?outletId=${encodeURIComponent(outletId)}` : ""}`,
        ),
        api<{ outlets: OwnerOutlet[] }>("/api/owner/outlets"),
      ]);
      setData(menu);
      setOutlets(outletResponse.outlets);
      if (!outletId && outletResponse.outlets[0]) {
        setOutletId(outletResponse.outlets[0].id);
      }
      setError(null);
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Tidak dapat memuat menu.",
      );
    } finally {
      setLoading(false);
    }
  }, [outletId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveCategory() {
    if (!categoryDraft || !categoryDraft.name.trim()) return;
    setBusy(true);
    try {
      const suffix = outletId ? `?outletId=${encodeURIComponent(outletId)}` : "";
      if (categoryDraft.id) {
        await api(`/api/owner/menu/categories/${categoryDraft.id}${suffix}`, {
          method: "PATCH",
          body: JSON.stringify({
            name: categoryDraft.name.trim(),
            sortOrder: Number(categoryDraft.sortOrder || 0),
          }),
        });
      } else {
        await api(`/api/owner/menu/categories${suffix}`, {
          method: "POST",
          body: JSON.stringify({
            name: categoryDraft.name.trim(),
            sortOrder: Number(categoryDraft.sortOrder || 0),
          }),
        });
      }
      setCategoryDraft(null);
      await load();
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Gagal menyimpan kategori.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function uploadMenuImage(file: File) {
    if (!itemDraft) return;
    if (file.size > 10 * 1024 * 1024) {
      setUploadError("Ukuran gambar maksimal 10 MB.");
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const result = await apiUpload<{ url: string }>(
        "/api/owner/menu/upload",
        form,
      );
      setItemDraft((current) =>
        current ? { ...current, imageUrl: result.url } : current,
      );
    } catch (requestError) {
      setUploadError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Gagal mengunggah gambar menu.",
      );
    } finally {
      setUploading(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  }

  async function saveItem() {
    if (!itemDraft || !itemDraft.name.trim() || !itemDraft.categoryId) return;
    setBusy(true);
    try {
      const suffix = outletId ? `?outletId=${encodeURIComponent(outletId)}` : "";
      const payload = {
        categoryId: itemDraft.categoryId,
        name: itemDraft.name.trim(),
        description: itemDraft.description.trim() || undefined,
        imageUrl: itemDraft.imageUrl.trim(),
        basePrice: Number(itemDraft.basePrice || 0),
        price: Number(itemDraft.price || itemDraft.basePrice || 0),
        station: itemDraft.station,
        available: itemDraft.available,
      };
      if (itemDraft.id) {
        await api(`/api/owner/menu/items/${itemDraft.id}${suffix}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
      } else {
        await api(`/api/owner/menu/items${suffix}`, {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }
      setItemDraft(null);
      await load();
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Gagal menyimpan menu.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function toggleAvailability(id: string, available: boolean) {
    try {
      const suffix = outletId ? `?outletId=${encodeURIComponent(outletId)}` : "";
      await api(`/api/owner/menu/items/${id}/availability${suffix}`, {
        method: "PATCH",
        body: JSON.stringify({ available }),
      });
      await load();
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Gagal mengubah ketersediaan.",
      );
    }
  }

  async function removeItem(id: string) {
    if (!window.confirm("Nonaktifkan menu ini? Riwayat pesanan tetap tersimpan.")) return;
    try {
      const suffix = outletId ? `?outletId=${encodeURIComponent(outletId)}` : "";
      await api(`/api/owner/menu/items/${id}${suffix}`, { method: "DELETE" });
      await load();
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Gagal menonaktifkan menu.",
      );
    }
  }

  const categories = data?.categories ?? [];
  const firstCategory = categories[0];

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Katalog"
        title="Menu & harga"
        description="Atur kategori, item, harga per outlet, dan ketersediaan. Perubahan langsung terlihat di halaman pelanggan."
        actions={
          <div className="kds-actions">
            <select
              className="input outlet-select"
              value={outletId}
              onChange={(event) => setOutletId(event.target.value)}
            >
              {outlets.map((outlet) => (
                <option key={outlet.id} value={outlet.id}>
                  {outlet.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setCategoryDraft({ name: "", sortOrder: String(categories.length) })}
            >
              <Plus size={16} /> Kategori
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!firstCategory}
              onClick={() => {
                const category = firstCategory;
                if (!category) return;
                setItemDraft({
                  ...emptyItem,
                  categoryId: category.id,
                  basePrice: "",
                });
              }}
            >
              <Plus size={16} /> Menu baru
            </button>
          </div>
        }
      />

      {error ? <div className="alert alert--danger">{error}</div> : null}

      {loading ? (
        <div className="empty-state empty-state--roomy">
          <UtensilsCrossed size={32} />
          <h3>Memuat menu...</h3>
        </div>
      ) : categories.length === 0 ? (
        <div className="empty-state empty-state--roomy">
          <UtensilsCrossed size={36} />
          <h3>Menu masih kosong</h3>
          <p>Mulai dengan membuat kategori, lalu tambahkan item ke dalamnya.</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setCategoryDraft({ name: "", sortOrder: "0" })}
          >
            <Plus size={16} /> Buat kategori pertama
          </button>
        </div>
      ) : (
        <div className="menu-admin">
          {categories.map((category) => (
            <section className="card card--pad menu-admin__section" key={category.id}>
              <header className="menu-admin__head">
                <div>
                  <h2>{category.name}</h2>
                  <span>{category.items.length} item · urutan {category.sortOrder}</span>
                </div>
                <div className="menu-admin__head-actions">
                  <button
                    type="button"
                    className="icon-button"
                    title="Ubah kategori"
                    onClick={() =>
                      setCategoryDraft({
                        id: category.id,
                        name: category.name,
                        sortOrder: String(category.sortOrder),
                      })
                    }
                  >
                    <Pencil size={15} />
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn--sm"
                    onClick={() =>
                      setItemDraft({
                        ...emptyItem,
                        categoryId: category.id,
                      })
                    }
                  >
                    <Plus size={15} /> Item
                  </button>
                </div>
              </header>

              {category.items.length === 0 ? (
                <p className="menu-admin__empty">Belum ada item di kategori ini.</p>
              ) : (
                <div className="menu-admin__items">
                  {category.items.map((item) => (
                    <article className="menu-admin__item" key={item.id}>
                      <div className="menu-admin__item-media">
                        <img
                          src={menuImageFor(item.name, item.imageUrl)}
                          alt={item.name}
                          loading="lazy"
                          onError={(event) => {
                            event.currentTarget.onerror = null;
                            event.currentTarget.src = menuImageFor(item.name, null);
                          }}
                        />
                      </div>
                      <div className="menu-admin__item-body">
                        <div className="menu-admin__item-head">
                          <strong>{item.name}</strong>
                          <span>{formatRupiah(item.price)}</span>
                        </div>
                        {item.description ? <p>{item.description}</p> : null}
                        <div className="menu-admin__item-meta">
                          <span className={`badge ${item.available ? "badge--success" : "badge--danger"}`}>
                            {item.available ? "Tersedia" : "Habis"}
                          </span>
                          <span className="badge badge--neutral">
                            {item.station === "bar" ? "Bar" : item.station === "both" ? "Dapur & bar" : "Dapur"}
                          </span>
                          {item.status === "inactive" ? (
                            <span className="badge badge--danger">Nonaktif</span>
                          ) : null}
                        </div>
                      </div>
                      <div className="menu-admin__item-actions">
                        <button
                          type="button"
                          className="btn btn-secondary btn--sm"
                          onClick={() =>
                            setItemDraft({
                              id: item.id,
                              categoryId: category.id,
                              name: item.name,
                              description: item.description ?? "",
                              imageUrl: item.imageUrl ?? "",
                              basePrice: String(item.basePrice),
                              price: String(item.price),
                              station: item.station as ItemDraft["station"],
                              available: item.available,
                            })
                          }
                        >
                          <Pencil size={14} /> Ubah
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn--sm"
                          onClick={() => void toggleAvailability(item.id, !item.available)}
                        >
                          {item.available ? "Set habis" : "Set tersedia"}
                        </button>
                        <button
                          type="button"
                          className="icon-button menu-admin__delete"
                          title="Nonaktifkan"
                          onClick={() => void removeItem(item.id)}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      )}

      {categoryDraft ? (
        <Modal title={categoryDraft.id ? "Ubah kategori" : "Kategori baru"} onClose={() => setCategoryDraft(null)}>
          <label className="field field--full">
            <span>Nama kategori</span>
            <input
              className="input"
              autoFocus
              value={categoryDraft.name}
              onChange={(event) =>
                setCategoryDraft({ ...categoryDraft, name: event.target.value })
              }
              placeholder="Contoh: Coffee"
            />
          </label>
          <label className="field field--full">
            <span>Urutan</span>
            <input
              className="input"
              inputMode="numeric"
              value={categoryDraft.sortOrder}
              onChange={(event) =>
                setCategoryDraft({ ...categoryDraft, sortOrder: event.target.value })
              }
            />
          </label>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setCategoryDraft(null)}>
              Batal
            </button>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void saveCategory()}>
              Simpan
            </button>
          </div>
        </Modal>
      ) : null}

      {itemDraft ? (
        <Modal title={itemDraft.id ? "Ubah menu" : "Menu baru"} onClose={() => setItemDraft(null)}>
          <div className="form-grid">
            <label className="field field--full">
              <span>Nama menu</span>
              <input
                className="input"
                autoFocus
                value={itemDraft.name}
                onChange={(event) => setItemDraft({ ...itemDraft, name: event.target.value })}
                placeholder="Contoh: Cafe Latte"
              />
            </label>
            <label className="field field--full">
              <span>Deskripsi</span>
              <textarea
                className="input"
                rows={2}
                value={itemDraft.description}
                onChange={(event) =>
                  setItemDraft({ ...itemDraft, description: event.target.value })
                }
              />
            </label>
            <div className="field field--full menu-image-field">
              <span>Gambar menu</span>
              <div className="menu-image-upload">
                <img
                  src={menuImageFor(itemDraft.name, itemDraft.imageUrl)}
                  alt=""
                  onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = menuImageFor(itemDraft.name, null);
                  }}
                />
                <div className="menu-image-upload__body">
                  <div className="menu-image-upload__actions">
                    <input
                      ref={imageInputRef}
                      className="menu-image-upload__input"
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (file) void uploadMenuImage(file);
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-secondary btn--sm"
                      onClick={() => imageInputRef.current?.click()}
                      disabled={uploading || busy}
                    >
                      {uploading ? (
                        <Loader2 className="spin" size={15} />
                      ) : (
                        <Upload size={15} />
                      )}
                      {uploading
                        ? "Mengunggah..."
                        : itemDraft.imageUrl
                          ? "Ganti gambar"
                          : "Pilih gambar"}
                    </button>
                    {itemDraft.imageUrl ? (
                      <button
                        type="button"
                        className="btn btn-ghost btn--sm"
                        onClick={() =>
                          setItemDraft({ ...itemDraft, imageUrl: "" })
                        }
                        disabled={uploading}
                      >
                        Hapus
                      </button>
                    ) : null}
                  </div>
                  <small className="fine-print">
                    JPG, PNG, WEBP, atau GIF · maksimal 10 MB. Tanpa gambar,
                    sistem memakai ilustrasi default sesuai nama menu.
                  </small>
                  {uploadError ? (
                    <small className="menu-image-upload__error">
                      {uploadError}
                    </small>
                  ) : null}
                </div>
              </div>
            </div>
            <label className="field">
              <span>Kategori</span>
              <select
                className="input"
                value={itemDraft.categoryId}
                onChange={(event) =>
                  setItemDraft({ ...itemDraft, categoryId: Number(event.target.value) })
                }
              >
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Stasiun</span>
              <select
                className="input"
                value={itemDraft.station}
                onChange={(event) =>
                  setItemDraft({
                    ...itemDraft,
                    station: event.target.value as ItemDraft["station"],
                  })
                }
              >
                <option value="kitchen">Dapur</option>
                <option value="bar">Bar</option>
                <option value="both">Dapur & bar</option>
              </select>
            </label>
            <label className="field">
              <span>Harga dasar</span>
              <input
                className="input"
                inputMode="numeric"
                value={itemDraft.basePrice}
                onChange={(event) =>
                  setItemDraft({ ...itemDraft, basePrice: event.target.value })
                }
                placeholder="22000"
              />
            </label>
            <label className="field">
              <span>Harga outlet</span>
              <input
                className="input"
                inputMode="numeric"
                value={itemDraft.price}
                onChange={(event) =>
                  setItemDraft({ ...itemDraft, price: event.target.value })
                }
                placeholder="22000"
              />
            </label>
            <label className="checkbox-row field--full">
              <input
                type="checkbox"
                checked={itemDraft.available}
                onChange={(event) =>
                  setItemDraft({ ...itemDraft, available: event.target.checked })
                }
              />
              <span>Tersedia untuk dijual</span>
            </label>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setItemDraft(null)}>
              Batal
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || uploading}
              onClick={() => void saveItem()}
            >
              Simpan menu
            </button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div className="sheet-backdrop" role="presentation" onClick={onClose}>
      <div
        className="sheet sheet--form"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="sheet__head">
          <div>
            <p className="eyebrow">Menu</p>
            <h2>{title}</h2>
          </div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Tutup">
            <X size={18} />
          </button>
        </div>
        <div className="sheet__body">{children}</div>
      </div>
    </div>
  );
}
